import { useLocation, useNavigate } from 'react-router-dom';

import { useLecturePlayerStore } from './lecturePlayer';
import type { BoardNavigationState } from './useBoardContext';

export interface UnitSelection {
  unitId: string;
  unitName: string;
  courseId?: string;
}

// swaps only the unit param and title; route state (class note) and the ink store are untouched
export function useSelectUnit(): (selection: UnitSelection) => void {
  const location = useLocation();
  const navigate = useNavigate();
  const expandPlayer = useLecturePlayerStore((state) => state.expand);
  return (selection) => {
    // picking a unit is the re-open: the filmstrip tile of a dismissed lecture brings its player back
    expandPlayer();
    const search = new URLSearchParams(location.search);
    search.set('unit', selection.unitId);
    const state = (location.state || {}) as BoardNavigationState;
    navigate(
      { pathname: location.pathname, search: `?${search.toString()}` },
      { replace: true, state: { ...state, lessonTitle: selection.unitName, courseId: selection.courseId, blankBoard: undefined } },
    );
  };
}

/**
 * Drops `?unit=` and goes back to the blank board.
 *
 * Not a document: with no unit id `LectureLoader` paints no background, `WhiteboardPage` keys its
 * ink on `blank`, and `boardStore` leaves `pageLimit` null - which is exactly what makes the page
 * appendable. Pinning a page count here would kill that, so nothing is configured at all.
 */
export function useClearUnit(): () => void {
  const location = useLocation();
  const navigate = useNavigate();
  return () => {
    const search = new URLSearchParams(location.search);
    search.delete('unit');
    const state = (location.state || {}) as BoardNavigationState;
    const query = search.toString();
    navigate(
      { pathname: location.pathname, search: query ? `?${query}` : '' },
      // marked, so the lecture-suggestion tray does not cover the surface the teacher just asked for
      { replace: true, state: { ...state, lessonTitle: undefined, courseId: undefined, blankBoard: true } },
    );
  };
}
